import {
  Component,
  inject,
  signal,
  input,
  output,
  OnInit,
  effect
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { DropdownModule } from 'primeng/dropdown';
import { InputTextarea } from 'primeng/inputtextarea';
import { CatalogoRepository } from 'src/app/domain/service/catalogo/catalogo-repository';
import { CatalogoOption } from 'src/app/domain/model/catalogo/catalogo';
import { sanitizeTextarea } from 'src/app/common/input-utils';
import { MAX_OBSERVACIONES_JUSTIFICACION_TARDIA } from 'src/app/common/constants-ejb';
import { ContadorCaracteresDirective } from 'src/app/presentation/shared/contador-caracteres/contador-caracteres.directive';

/**
 * Resultado del diálogo de justificación tardía
 */
export interface JustificacionTardiaResult {
  /** Código de justificación seleccionado (TPSC_JUSTIFICACIONES.CDJUSTIFICACION) */
  cdJustificacion: number;
  /** Descripción de la justificación seleccionada */
  dsJustificacion: string;
  /** Observaciones del usuario */
  observaciones: string;
}

/**
 * JustificacionTardiaDialogComponent
 *
 * Diálogo para capturar justificación cuando se crea o edita una cita tardía
 * (fecha de programación supera LIMIT_DAYS_PROGRAM días hábiles).
 *
 * Migrado del legacy: CreateOrUpdateCumplimientoViewImpl.java
 * - openDialogTardio()
 * - cumplimientoTardio()
 * - mapearJustificacionTardia()
 *
 * @author Generado desde migración GIROP
 */
@Component({
  selector: 'app-justificacion-tardia-dialog',
  standalone: true,
  imports: [
    ContadorCaracteresDirective,
    CommonModule,
    FormsModule,
    DialogModule,
    ButtonModule,
    DropdownModule,
    InputTextarea
  ],
  template: `
    <p-dialog
      [visible]="visible()"
      header="Justificación de Programación Tardía"
      [modal]="true"
      [style]="{ width: '92vw', maxWidth: '32rem' }"
      [contentStyle]="{ 'max-height': '80vh', 'overflow-y': 'auto' }"
      [breakpoints]="{ '768px': '94vw', '640px': '96vw' }"
      [closable]="true"
      (onHide)="onCancelar()"
      (visibleChange)="onVisibleChange($event)"
    >
      <div class="flex flex-col gap-4">
        <!-- Mensaje informativo -->
        <div
          class="p-3 bg-yellow-50 dark:bg-yellow-900/30 border-l-4 border-yellow-400 dark:border-yellow-600 text-yellow-800 dark:text-yellow-200 rounded"
        >
          <p class="text-sm">
            <i class="pi pi-exclamation-triangle mr-2"></i>
            La fecha de programación supera el límite de días permitidos. Por
            favor, seleccione una justificación y agregue observaciones.
          </p>
        </div>

        <!-- Dropdown de justificaciones -->
        <div class="flex flex-col gap-1">
          <label for="justificacion" class="font-medium text-sm">
            Justificación <span class="text-red-500">*</span>
          </label>
          <p-dropdown
            id="justificacion"
            [options]="justificaciones()"
            [(ngModel)]="justificacionSeleccionada"
            optionLabel="descripcion"
            optionValue="codigo"
            placeholder="Seleccione una justificación"
            [showClear]="true"
            [filter]="true"
            filterBy="descripcion"
            styleClass="w-full"
            [loading]="cargandoJustificaciones()"
          >
          </p-dropdown>
        </div>

        <!-- Textarea de observaciones -->
        <div class="flex flex-col gap-1">
          <label for="observaciones" class="font-medium text-sm">
            Observaciones <span class="text-red-500">*</span>
          </label>
          <textarea
            pInputTextarea
            id="observaciones"
            [(ngModel)]="observaciones"
            (change)="observaciones = sanitizeTextarea(observaciones)"
            rows="4"
            placeholder="Ingrese las observaciones de la justificación"
            class="w-full"
            [appContadorCaracteres]="maxObservaciones"
          >
          </textarea>
          <small class="text-gray-500 text-right"
            >{{ observaciones.length }}/500</small
          >
        </div>

        <!-- Mensaje de error -->
        @if (mensajeError()) {
          <div
            class="p-2 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 text-red-700 dark:text-red-300 rounded text-sm"
          >
            <i class="pi pi-times-circle mr-2"></i>
            {{ mensajeError() }}
          </div>
        }
      </div>

      <!-- Footer con botones -->
      <ng-template pTemplate="footer">
        <div
          class="flex flex-wrap justify-end sm:justify-start gap-2 sm:gap-3 w-full"
        >
          <p-button
            label="Cancelar"
            icon="pi pi-times"
            [outlined]="true"
            styleClass="min-h-[44px]"
            (onClick)="onCancelar()"
          >
          </p-button>
          <p-button
            label="Continuar"
            icon="pi pi-check"
            styleClass="min-h-[44px]"
            (onClick)="onConfirmar()"
            [disabled]="!esFormularioValido()"
          >
          </p-button>
        </div>
      </ng-template>
    </p-dialog>
  `
})
export class JustificacionTardiaDialogComponent implements OnInit {
  readonly maxObservaciones = MAX_OBSERVACIONES_JUSTIFICACION_TARDIA;
  // Inputs
  visible = input.required<boolean>();

  // Outputs
  onClose = output<void>();
  onConfirm = output<JustificacionTardiaResult>();

  // Inyecciones
  private catalogoRepository = inject(CatalogoRepository);

  // Estado
  justificaciones = signal<CatalogoOption[]>([]);
  cargandoJustificaciones = signal(false);
  mensajeError = signal<string | null>(null);

  // Form fields
  justificacionSeleccionada: string | null = null;
  observaciones = '';

  // XSS Sanitization - exponer función al template
  sanitizeTextarea = sanitizeTextarea;

  constructor() {
    // Cargar justificaciones cuando el diálogo se hace visible
    effect(() => {
      if (this.visible()) {
        this.cargarJustificaciones();
        this.resetForm();
      }
    });
  }

  ngOnInit(): void {
    // Las justificaciones se cargan cuando el diálogo es visible
  }

  /**
   * Carga las justificaciones desde TPSC_JUSTIFICACIONES.
   * Reutiliza el mismo endpoint que el diálogo de cancelación.
   */
  private async cargarJustificaciones(): Promise<void> {
    if (this.justificaciones().length > 0) {
      return; // Ya están cargadas
    }

    this.cargandoJustificaciones.set(true);
    try {
      const justificaciones =
        await this.catalogoRepository.getJustificacionesCancelacion();
      this.justificaciones.set(justificaciones);
    } catch (error) {
      this.mensajeError.set(
        'Error al cargar las justificaciones. Por favor, intente nuevamente.'
      );
    } finally {
      this.cargandoJustificaciones.set(false);
    }
  }

  /**
   * Resetea el formulario a su estado inicial.
   */
  private resetForm(): void {
    this.justificacionSeleccionada = null;
    this.observaciones = '';
    this.mensajeError.set(null);
  }

  /**
   * Verifica si el formulario es válido.
   */
  esFormularioValido(): boolean {
    return (
      !!this.justificacionSeleccionada && this.observaciones.trim().length > 0
    );
  }

  /**
   * Handler para el botón Cancelar.
   */
  onCancelar(): void {
    this.resetForm();
    this.onClose.emit();
  }

  /**
   * Handler para cambios de visibilidad del diálogo.
   */
  onVisibleChange(visible: boolean): void {
    if (!visible) {
      this.onCancelar();
    }
  }

  /**
   * Handler para el botón Confirmar.
   */
  onConfirmar(): void {
    // Validar formulario
    if (!this.justificacionSeleccionada) {
      this.mensajeError.set('Debe seleccionar una justificación.');
      return;
    }

    if (!this.observaciones.trim()) {
      this.mensajeError.set('Debe ingresar las observaciones.');
      return;
    }

    // Buscar la descripción de la justificación seleccionada
    const justificacion = this.justificaciones().find(
      (j) => j.codigo === this.justificacionSeleccionada
    );

    const result: JustificacionTardiaResult = {
      cdJustificacion: parseInt(this.justificacionSeleccionada, 10),
      dsJustificacion: justificacion?.descripcion || '',
      observaciones: this.observaciones.trim()
    };

    this.onConfirm.emit(result);
  }
}
